import {inject, Injectable} from '@angular/core';
import {AdminContextService, GrantRestrictionLevel} from "@app/security/admin-context.service";

export abstract class RestrictedElement {
  private _adminContext = inject(AdminContextService);

  protected constructor(
    public readonly featureCode: string,
    public readonly restrictionLevel: GrantRestrictionLevel,
  ) {
  }

  public get hasAccess(): boolean {
    return this._adminContext.checkAccessByRestrictionLevel(this.featureCode, this.restrictionLevel);
  }

}

export class MenuModule extends RestrictedElement {
  constructor(
    public code: string,
    public label: string,
    private _sections: MenuSection[],
    featureCode: string,
    restrictionLevel: GrantRestrictionLevel,
    public icon_font?: string,
  ) {
    super(featureCode, restrictionLevel);
  }

  /**
   * Filter out sections that user does not have access to based on the feature code and security context
   */
  get sections(): MenuSection[] {
    return this._sections.filter(f => f.hasAccess);
  }
}

export class MenuSection extends RestrictedElement {
  selected: boolean = false;

  constructor(
    public code: string,
    public label: string,
    public icon: string,
    featureCode: string,
    restrictionLevel: GrantRestrictionLevel,
    public routerLink?: any,
    private _sectionItems: MenuSectionItem[] = [],
  ) {
    super(featureCode, restrictionLevel);

    if (!_sectionItems) {
      this._sectionItems = [];
    }
  }

  get sectionItems(): MenuSectionItem[] {
    return this._sectionItems.filter(f => f.hasAccess);
  }

  public get hasSectionItems(): boolean {
    return this.sectionItems.length > 0;
  }
}


export class MenuSectionItem extends RestrictedElement {
  constructor(
    public code: string,
    public label: string,
    public routerLink: any,
    featureCode: string,
    restrictionLevel: GrantRestrictionLevel,
    public icon?: string
  ) {
    super(featureCode, restrictionLevel);
  }

}


@Injectable({
  providedIn: 'root'
})
export class AdminMenuService {
  private readonly _adminMenuConfig: MenuModule[] = [
    // ── GLOBAL ──────────────────────────────────────────────────────────────
    new MenuModule(
      'admin_feature_config',
      'Feature Config',
      [
        new MenuSection('admin_feature_config_modules', 'Modules', 'collections_bookmark', 'rbac_manage_roles', 'GLOBAL', ['/feature-config/modules']),
        new MenuSection('admin_feature_config_roles', 'Roles', 'bookmarks', 'rbac_manage_roles', 'GLOBAL', ['/feature-config/roles']),
        new MenuSection('admin_feature_config_features', 'Features', 'bookmark', 'rbac_manage_roles', 'GLOBAL', ['/feature-config/features']),
      ],
      'rbac_manage_roles',
      'GLOBAL',
      'collections_bookmark',
    ),

    new MenuModule(
      'admin_internal_access',
      'Internal Access',
      [
        new MenuSection('admin_internal_access_members', 'Internal Access', 'admin_panel_settings', 'admin_super_users_edit', 'GLOBAL', ['/internal-access']),
      ],
      'admin_super_users_edit',
      'GLOBAL',
      'admin_panel_settings',
    ),

    // ── LANDLORD ─────────────────────────────────────────────────────────────
    new MenuModule(
      'admin_landlord',
      'Landlord',
      [
        new MenuSection('admin_landlord_settings', 'Settings', 'settings', 'admin_onboarding_view', 'LANDLORD', undefined, [
          new MenuSectionItem('admin_landlord_settings_details', 'Details', ['/landlord/settings/details'], 'admin_onboarding_view', 'LANDLORD'),
          new MenuSectionItem('admin_landlord_settings_contacts', 'Contacts', ['/landlord/settings/contacts'], 'admin_onboarding_view', 'LANDLORD'),
          new MenuSectionItem('admin_landlord_settings_locations', 'Locations', ['/landlord/settings/locations'], 'admin_onboarding_view', 'LANDLORD'),
          new MenuSectionItem('admin_landlord_settings_access', 'Access', ['/landlord/settings/access'], 'admin_access_view', 'LANDLORD'),
        ]),
      ],
      'admin_onboarding_view',
      'LANDLORD',
      'domain',
    ),

    // ── LOCATION ─────────────────────────────────────────────────────────────
    new MenuModule(
      'admin_location',
      'Location',
      [
        new MenuSection('admin_location_settings', 'Settings', 'settings', 'admin_onboarding_view', 'LOCATION', undefined, [
          new MenuSectionItem('admin_location_settings_details', 'Details', ['/location/settings/details'], 'admin_onboarding_view', 'LOCATION'),
          new MenuSectionItem('admin_location_settings_contacts', 'Contacts', ['/location/settings/contacts'], 'admin_onboarding_view', 'LOCATION'),
          new MenuSectionItem('admin_location_settings_offerings', 'Offerings', ['/location/settings/offerings'], 'admin_onboarding_view', 'LOCATION'),
          new MenuSectionItem('admin_location_settings_access', 'Access', ['/location/settings/access'], 'admin_access_view', 'LOCATION'),
        ]),
      ],
      'admin_onboarding_view',
      'LOCATION',
      'location_on',
    ),

    // ── OFFERING ─────────────────────────────────────────────────────────────
    new MenuModule(
      'admin_offering',
      'Offering',
      [
        new MenuSection('admin_offering_settings', 'Settings', 'settings', 'admin_onboarding_view', 'OFFERING', undefined, [
          new MenuSectionItem('admin_offering_settings_readiness', 'Readiness', ['/offering/settings/readiness'], 'admin_onboarding_view', 'OFFERING'),
          new MenuSectionItem('admin_offering_settings_details', 'Details', ['/offering/settings/details'], 'admin_onboarding_view', 'OFFERING'),
          new MenuSectionItem('admin_offering_settings_contacts', 'Contacts', ['/offering/settings/contacts'], 'admin_onboarding_view', 'OFFERING'),
          new MenuSectionItem('admin_offering_settings_recommendation', 'Recommendation Config', ['/offering/settings/recommendation'], 'admin_onboarding_view', 'OFFERING'),
          new MenuSectionItem('admin_offering_settings_billing', 'Billing', ['/offering/settings/billing-banking'], 'admin_billing_banking_view', 'OFFERING'),
          new MenuSectionItem('admin_offering_settings_listing', 'Listing & Industries', ['/offering/settings/listing'], 'admin_industries_edit', 'OFFERING'),
        ]),
        new MenuSection('admin_offering_integrated_pos', 'Integrated POS', 'point_of_sale', 'admin_pos_connections_edit', 'OFFERING', undefined, [
          new MenuSectionItem('admin_offering_settings_payments', 'Setup', ['/offering/settings/payments'], 'admin_pos_connections_edit', 'OFFERING'),
          new MenuSectionItem('admin_offering_settings_imports', 'Imports', ['/offering/settings/imports'], 'admin_pos_connections_edit', 'OFFERING'),
          new MenuSectionItem('admin_offering_settings_pos', 'POS Connections', ['/offering/settings/pos'], 'admin_pos_connections_edit', 'OFFERING'),
        ]),
        new MenuSection('admin_offering_users', 'Users & Access', 'badge', 'admin_access_view', 'OFFERING', undefined, [
          new MenuSectionItem('admin_offering_settings_access', 'Access', ['/offering/settings/access'], 'admin_access_view', 'OFFERING'),
          new MenuSectionItem('admin_offering_role_groups', 'Role groups', ['/rbac/offering/role-groups'], 'admin_access_view', 'OFFERING'),
          new MenuSectionItem('admin_offering_roles', 'Roles', ['/rbac/offering/roles'], 'admin_access_view', 'OFFERING'),
        ]),
        new MenuSection('admin_transactions', 'Transactions', 'receipt_long', 'admin_onboarding_view', 'OFFERING', undefined, [
          new MenuSectionItem('admin_transactions_statements', 'Retailer Statements', ['/transactions/statements'], 'admin_onboarding_view', 'OFFERING'),
          new MenuSectionItem('admin_external_accounting_software', 'External Software Reports', ['/transactions/external-accounts/software'], 'admin_onboarding_view', 'OFFERING'),
        ]),
        new MenuSection('admin_leasing', 'Leasing', 'home_work', 'admin_onboarding_view', 'OFFERING', undefined, [
          new MenuSectionItem('admin_leasing_lease_upload', 'Lease Uploads', ['/leasing/lease-upload'], 'admin_onboarding_view', 'OFFERING'),
          new MenuSectionItem('admin_leasing_billing_upload', 'Billing Uploads', ['/leasing/billing-upload'], 'admin_onboarding_view', 'OFFERING'),
          new MenuSectionItem('admin_leasing_lease_pos', 'Point-of-Sale', ['/leasing/lease-pos'], 'admin_onboarding_view', 'OFFERING'),
        ]),
        // CS-823 FE-17 (spec 4.13): gated on the BE-14 admin_jobs_view feature.
        new MenuSection('admin_jobs', 'Jobs', 'settings', 'admin_jobs_view', 'OFFERING', undefined, [
          new MenuSectionItem('admin_jobs_configs', 'Job Run Configs', ['/jobs/configs'], 'admin_jobs_view', 'OFFERING'),
        ]),
      ],
      'admin_onboarding_view',
      'OFFERING',
      'storefront',
    ),
  ];

  constructor() {
  }

  /**
   * Gets the menu items, filtered by the user's access rights.
   *
   * Filtered dynamically (not cached): module `hasAccess` for the contextual
   * LANDLORD/LOCATION/OFFERING modules depends on the currently selected admin
   * context, and callers re-invoke this on every context change.
   */
  public getMenu(): MenuModule[] {
    return this._adminMenuConfig.filter(f => f.hasAccess);
  }

}
